import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const curFy = () => { const n = new Date(); const s = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; return `${s}-${String((s + 1) % 100).padStart(2, "0")}`; };
const curQ = () => { const m = new Date().getMonth(); return m >= 3 && m <= 5 ? 1 : m <= 8 && m >= 6 ? 2 : m >= 9 ? 3 : 4; };
const fys = () => { const s = Number(curFy().slice(0, 4)); return [0, 1, 2].map((i) => `${s - i}-${String((s - i + 1) % 100).padStart(2, "0")}`); };
const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";

const waText = (r, d, link) =>
  `Dear ${r.payee_name},\n\nYour TDS certificate for FY ${d.fy} Q${d.quarter} (${d.from} to ${d.to}) from Connect Tym is ready.\n\n` +
  `Certificate No: ${r.certificate_no}\nPAN: ${r.pan || "Not available"}\nSection: ${r.section}\nAmount paid: ${inr(r.gross)}\nTDS deducted: ${inr(r.tds)}\n\n` +
  `Download (valid 30 days): ${link}\n\nThis is a provisional certificate; the official Form 16A will be shared after it is issued on TRACES.`;

export function TdsCertificates() {
  const { can } = useAuth();
  const [fy, setFy] = useState(curFy());
  const [q, setQ] = useState(curQ());
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tax/certificates", { params: { fy, quarter: q } }).then(({ data }) => setD(data)).catch(() => setD(null)); }, [fy, q]);

  const pdf = (r) => window.open(`${API}/tax/certificates/${r.category}/${r.payee_id}/pdf?fy=${fy}&quarter=${q}&token=${getToken()}`, "_blank");
  const share = async (r) => {
    const w = window.open("", "_blank");
    try {
      const { data } = await api.get(`/tax/certificates/${r.category}/${r.payee_id}/share`, { params: { fy, quarter: q } });
      const link = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      const dg = (r.mobile || "").replace(/\D/g, "");
      const url = `https://wa.me/${dg.length === 10 ? `91${dg}` : dg}?text=${encodeURIComponent(waText(r, d, link))}`;
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="tds-certificates">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={sel} value={fy} onChange={(e) => setFy(e.target.value)} data-testid="cert-fy">{fys().map((f) => <option key={f} value={f}>FY {f}</option>)}</select>
        <select className={sel} value={q} onChange={(e) => setQ(Number(e.target.value))} data-testid="cert-quarter">
          {[1, 2, 3, 4].map((x) => <option key={x} value={x}>Q{x} ({["Apr–Jun", "Jul–Sep", "Oct–Dec", "Jan–Mar"][x - 1]})</option>)}</select>
        {d && <span className="text-xs text-muted-foreground">{d.from} → {d.to} · {d.rows.length} certificate(s)</span>}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Provisional certificates in Form 16A format. The official Form 16A must be downloaded from TRACES after filing the quarterly TDS return. Set deductor TAN/PAN in Settings → Tax & TDS.</p>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">{["Certificate No.", "Payee", "PAN", "Section", "Amount Paid", "TDS", "Deposited", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {d && d.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No TDS deducted in this quarter</TableCell></TableRow>}
            {d?.rows.map((r) => (
              <TableRow key={r.certificate_no} data-testid={`cert-row-${r.certificate_no}`}>
                <TableCell className="font-mono text-xs">{r.certificate_no}</TableCell>
                <TableCell><p className="font-medium">{r.payee_name}</p><p className="text-[11px] text-muted-foreground">{r.category === "dp" ? "Delivery Partner" : "Franchise"} · {r.ref_code}</p></TableCell>
                <TableCell className="font-mono text-xs">{r.pan || "—"}</TableCell>
                <TableCell className="text-xs">{r.section}</TableCell>
                <TableCell className="tabular">{inr(r.gross)}</TableCell>
                <TableCell className="tabular font-semibold text-rose-500">{inr(r.tds)}</TableCell>
                <TableCell className={r.fully_deposited ? "text-xs font-semibold text-emerald-500" : "text-xs font-semibold text-amber-500"}>{r.fully_deposited ? "Yes" : `Pending ${inr(r.tds - r.deposited)}`}</TableCell>
                <TableCell><div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => pdf(r)} data-testid={`cert-pdf-${r.certificate_no}`}><Download className="mr-1 h-3 w-3" />PDF</Button>
                  {can("tax.download") && <Button size="sm" className="h-7 bg-emerald-600 px-2 text-xs text-white hover:bg-emerald-700" onClick={() => share(r)} data-testid={`cert-wa-${r.certificate_no}`}><MessageCircle className="mr-1 h-3 w-3" />WhatsApp</Button>}
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
